import { useState, useEffect, useMemo } from 'react'
import {
    Box,
    Card,
    Grid,
    Toolbar,
    IconButton,
    Select,
    MenuItem,
    TextField,
    Button,
    Typography,
    Breadcrumbs,
    Link,
    Dialog,
    DialogContent,
    DialogTitle,
    CircularProgress,
    CardMedia,
    CardContent,
    CardActions
} from '@mui/material'
import { ArrowBackIos, Close, Folder as FolderIcon, InsertDriveFile as FileIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useLanguage } from '../../context'
import azureStorageRoutes from '../../routes/azure-storage.routes'
import './style.css'

const GaleryPage = () => {
    const navigate = useNavigate()
    const { dictionary } = useLanguage()

    // State variables
    const [container, setContainer] = useState('images')
    const [prefix, setPrefix] = useState('')
    const [search, setSearch] = useState('')
    const [otSearch, setOtSearch] = useState('')
    const [otMode, setOtMode] = useState(false)
    const [activeOt, setActiveOt] = useState(null)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [files, setFiles] = useState([])
    const [folders, setFolders] = useState([])
    const [continuationToken, setContinuationToken] = useState(null)
    const [previewItem, setPreviewItem] = useState(null)
    const [sortBy, setSortBy] = useState('name-asc')
    const [visibleCount, setVisibleCount] = useState(50)

    // Sort files based on sortBy
    const sortedFiles = useMemo(() => {
        const sorted = [...files]
        if (sortBy === 'name-asc') {
            sorted.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }))
        } else if (sortBy === 'name-desc') {
            sorted.sort((a, b) => b.name.localeCompare(a.name, undefined, { sensitivity: 'base', numeric: true }))
        } else if (sortBy === 'date-desc') {
            sorted.sort((a, b) => {
                const dateA = a.lastModified ? new Date(a.lastModified) : 0
                const dateB = b.lastModified ? new Date(b.lastModified) : 0
                return dateB - dateA
            })
        } else if (sortBy === 'date-asc') {
            sorted.sort((a, b) => {
                const dateA = a.lastModified ? new Date(a.lastModified) : 0
                const dateB = b.lastModified ? new Date(b.lastModified) : 0
                return dateA - dateB
            })
        }
        return sorted
    }, [files, sortBy])

    // Sort folders based on sortBy (folders only sort by name)
    const sortedFolders = useMemo(() => {
        const sorted = [...folders]
        if (sortBy === 'name-desc') {
            sorted.sort((a, b) => b.localeCompare(a, undefined, { sensitivity: 'base', numeric: true }))
        } else {
            // Default to name-asc for folders as they don't have dates
            sorted.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }))
        }
        return sorted
    }, [folders, sortBy])

    // Rendered files sliced to visibleCount
    const renderedFiles = useMemo(() => {
        return sortedFiles.slice(0, visibleCount)
    }, [sortedFiles, visibleCount])

    // Load initial directory contents
    const loadDirectory = async (containerName, folderPrefix = '', cleanLoad = true, currentToken = null) => {
        if (cleanLoad) {
            setLoading(true)
            setFiles([])
            setFolders([])
            setContinuationToken(null)
            setVisibleCount(50)
        } else {
            setLoadingMore(true)
        }

        try {
            const response = await azureStorageRoutes.listBlobs(
                containerName,
                folderPrefix,
                cleanLoad ? search : '', // only search on clean load if search query is active
                5000,
                currentToken
            )

            if (response.data) {
                const { files: newFiles, folders: newFolders, continuationToken: newToken } = response.data

                if (cleanLoad) {
                    setFiles(newFiles || [])
                    setFolders(newFolders || [])
                } else {
                    setFiles(prev => [...prev, ...(newFiles || [])])
                }
                setContinuationToken(newToken)
            }
        } catch (error) {
            console.error('Error loading directory:', error)
        } finally {
            setLoading(false)
            setLoadingMore(false)
        }
    }

    // Search for images of a specific OT
    const loadOtImages = async (otId) => {
        setLoading(true)
        setOtMode(true)
        setActiveOt(otId)
        setFiles([])
        setFolders([])
        setContinuationToken(null)
        setVisibleCount(50)

        try {
            const response = await azureStorageRoutes.getOtImages(otId)
            if (response.data) {
                setFiles(response.data.map(img => ({
                    name: img.name,
                    url: img.url,
                    contentType: 'image/jpeg' // default mapping for rendering preview
                })))
            }
        } catch (error) {
            console.error('Error fetching OT images:', error)
            alert(`No se pudieron encontrar imágenes para la OT ${otId}`)
        } finally {
            setLoading(false)
        }
    }

    // React to container or prefix change (only in general mode)
    useEffect(() => {
        if (!otMode) {
            loadDirectory(container, prefix, true)
        }
    }, [container, prefix, otMode])

    // Handle search submission
    const handleSearch = (e) => {
        e.preventDefault()
        setOtMode(false)
        setActiveOt(null)
        loadDirectory(container, prefix, true)
    }

    // Handle OT search submission
    const handleOtSearch = (e) => {
        e.preventDefault()
        if (otSearch.trim()) {
            loadOtImages(otSearch.trim())
        }
    }

    // Clear OT mode and restore normal gallery
    const handleClearOtMode = () => {
        setOtMode(false)
        setActiveOt(null)
        setOtSearch('')
        setPrefix('')
    }

    // Load more files (incremental)
    const handleLoadMore = () => {
        if (visibleCount < sortedFiles.length) {
            setVisibleCount(prev => prev + 50)
        } else if (continuationToken && !loadingMore) {
            loadDirectory(container, prefix, false, continuationToken)
        }
    }

    // Format file sizes
    const formatBytes = (bytes, decimals = 2) => {
        if (!bytes) return '0 Bytes'
        const k = 1024
        const dm = decimals < 0 ? 0 : decimals
        const sizes = ['Bytes', 'KB', 'MB', 'GB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    }

    // Format last modified date
    const formatDate = (dateString) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    // Handle breadcrumb click
    const handleBreadcrumbClick = (partIndex) => {
        const parts = prefix.split('/').filter(Boolean)
        if (partIndex === -1) {
            setPrefix('')
        } else {
            const newPrefix = parts.slice(0, partIndex + 1).join('/') + '/'
            setPrefix(newPrefix)
        }
    }

    // Render breadcrumbs
    const renderBreadcrumbs = () => {
        const parts = prefix.split('/').filter(Boolean)
        return (
            <Breadcrumbs separator="/" aria-label="breadcrumb" sx={{ margin: '15px 0' }}>
                <Link
                    component="button"
                    underline="hover"
                    color="inherit"
                    onClick={() => handleBreadcrumbClick(-1)}
                    sx={{ fontWeight: parts.length === 0 ? 'bold' : 'normal' }}
                >
                    Inicio
                </Link>
                {parts.map((part, index) => {
                    const isLast = index === parts.length - 1
                    return (
                        <Typography
                            key={index}
                            color={isLast ? 'text.primary' : 'inherit'}
                            sx={{ fontWeight: isLast ? 'bold' : 'normal' }}
                        >
                            {part}
                        </Typography>
                    )
                })}
            </Breadcrumbs>
        )
    }

    // Helper to identify if a file is an image
    const isImageFile = (file) => {
        const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']
        const extension = file.name.split('.').pop()?.toLowerCase()
        return imageExtensions.includes(extension) || file.contentType?.startsWith('image/')
    }

    return (
        <Box height="100%">
            <Grid className={'root'} container spacing={0}>
                <Grid className={'pageContainer'} item xs={12}>
                    <Card elevation={0} className={'pageCard'}>

                        {/* Header toolbar */}
                        <Grid container alignItems="center" justifyContent="center">
                            <div style={{ width: '100%', textAlign: 'left', padding: 10 }}>
                                <div style={{ width: '100%', textAlign: 'left', color: '#333', backgroundColor: '#fff', borderRadius: 20 }}>
                                    <Toolbar style={{ paddingLeft: 0, backgroundColor: '#F9F9F9', borderRadius: 10 }}>
                                        <IconButton onClick={() => navigate('/administration')}>
                                            <ArrowBackIos style={{ color: '#333', fontSize: 16 }} />
                                        </IconButton>
                                        <h1 style={{ marginTop: 0, marginBottom: 0, fontSize: 16, flexGrow: 1 }}>
                                            Administración / Galería de Almacenamiento
                                        </h1>
                                        {otMode && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleClearOtMode}
                                                style={{ marginRight: 10, borderColor: '#BE2E26', color: '#BE2E26' }}
                                            >
                                                Volver a la Galería General
                                            </Button>
                                        )}
                                    </Toolbar>
                                </div>
                            </div>
                        </Grid>

                        {/* Search and Filters */}
                        <Box sx={{ padding: '0 20px', marginBottom: 2 }}>
                            <Grid container spacing={2} alignItems="center">
                                {!otMode ? (
                                    <>
                                        {/* Container selector */}
                                        <Grid item xs={12} sm={2.5}>
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={container}
                                                onChange={(e) => {
                                                    setContainer(e.target.value)
                                                    setPrefix('')
                                                }}
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="images">Imágenes de Reportes</MenuItem>
                                                <MenuItem value="pdfs">PDFs de Reportes</MenuItem>
                                                <MenuItem value="documents">Documentos Técnicos</MenuItem>
                                                <MenuItem value="news">Noticias (Wall Journal)</MenuItem>
                                            </Select>
                                        </Grid>

                                        {/* Name Search */}
                                        <Grid item xs={12} sm={4} component="form" onSubmit={handleSearch}>
                                            <Box display="flex">
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder="Buscar archivos por nombre..."
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px 0 0 10px' } }}
                                                />
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: '#BE2E26',
                                                        '&:hover': { backgroundColor: '#9e221b' },
                                                        borderRadius: '0 10px 10px 0',
                                                        boxShadow: 'none'
                                                    }}
                                                >
                                                    Buscar
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {/* Order By selector */}
                                        <Grid item xs={12} sm={2.5}>
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="name-asc">Nombre: A-Z</MenuItem>
                                                <MenuItem value="name-desc">Nombre: Z-A</MenuItem>
                                                <MenuItem value="date-desc">Fecha: Más reciente</MenuItem>
                                                <MenuItem value="date-asc">Fecha: Más antigua</MenuItem>
                                            </Select>
                                        </Grid>

                                        {/* OT Search */}
                                        <Grid item xs={12} sm={3} component="form" onSubmit={handleOtSearch}>
                                            <Box display="flex">
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder="Buscar OT (ej. 1000)"
                                                    value={otSearch}
                                                    onChange={(e) => setOtSearch(e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px 0 0 10px' } }}
                                                />
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: '#333333',
                                                        '&:hover': { backgroundColor: '#1a1a1a' },
                                                        borderRadius: '0 10px 10px 0',
                                                        boxShadow: 'none',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Buscar OT
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={12} sm={6.5}>
                                            <Box display="flex" alignItems="center" sx={{ backgroundColor: '#FFF5F5', padding: '8px 16px', borderRadius: '10px', border: '1px solid #FFD3D3' }}>
                                                <Typography variant="body1" sx={{ color: '#BE2E26', fontWeight: 'bold', flexGrow: 1 }}>
                                                    Mostrando imágenes de la OT: {activeOt}
                                                </Typography>
                                                <Button size="small" variant="text" onClick={handleClearOtMode} style={{ color: '#BE2E26' }}>
                                                    Cerrar búsqueda de OT
                                                </Button>
                                            </Box>
                                        </Grid>

                                        {/* Order By selector */}
                                        <Grid item xs={12} sm={2.5}>
                                            <Select
                                                fullWidth
                                                size="small"
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                                sx={{ borderRadius: '10px' }}
                                            >
                                                <MenuItem value="name-asc">Nombre: A-Z</MenuItem>
                                                <MenuItem value="name-desc">Nombre: Z-A</MenuItem>
                                                <MenuItem value="date-desc">Fecha: Más reciente</MenuItem>
                                                <MenuItem value="date-asc">Fecha: Más antigua</MenuItem>
                                            </Select>
                                        </Grid>

                                        {/* OT Search */}
                                        <Grid item xs={12} sm={3} component="form" onSubmit={handleOtSearch}>
                                            <Box display="flex">
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    placeholder="Buscar OT (ej. 1000)"
                                                    value={otSearch}
                                                    onChange={(e) => setOtSearch(e.target.value)}
                                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px 0 0 10px' } }}
                                                />
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    sx={{
                                                        backgroundColor: '#333333',
                                                        '&:hover': { backgroundColor: '#1a1a1a' },
                                                        borderRadius: '0 10px 10px 0',
                                                        boxShadow: 'none',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Buscar OT
                                                </Button>
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>

                        {/* Breadcrumbs (only in General Mode) */}
                        {!otMode && (
                            <Box sx={{ padding: '0 20px' }}>
                                {renderBreadcrumbs()}
                            </Box>
                        )}

                        {/* Folder and File List Container */}
                        <div style={{ width: '100%', textAlign: 'left', padding: '0 20px 20px 20px', overflowY: 'auto', height: 'calc(100vh - 280px)' }}>
                            {loading ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="300px">
                                    <CircularProgress style={{ color: '#BE2E26' }} />
                                    <Typography variant="body2" sx={{ marginTop: 2 }}>Cargando galería...</Typography>
                                </Box>
                            ) : (
                                <>
                                    {/* Folders Grid */}
                                    {sortedFolders.length > 0 && (
                                        <Box sx={{ marginBottom: 4 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#555' }}>
                                                Carpetas
                                            </Typography>
                                            <Grid container spacing={2}>
                                                {sortedFolders.map((folderName, index) => {
                                                    // Get the relative folder name from full path
                                                    const displayFolderName = folderName.replace(prefix, '').replace('/', '')
                                                    return (
                                                        <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
                                                            <Card
                                                                onClick={() => setPrefix(folderName)}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '12px 16px',
                                                                    cursor: 'pointer',
                                                                    borderRadius: '12px',
                                                                    border: '1px solid #eaeaea',
                                                                    boxShadow: 'none',
                                                                    transition: '0.2s',
                                                                    '&:hover': {
                                                                        backgroundColor: '#F9F9F9',
                                                                        borderColor: '#BE2E26',
                                                                        transform: 'translateY(-2px)'
                                                                    }
                                                                }}
                                                            >
                                                                <FolderIcon sx={{ color: '#FAD165', marginRight: 1.5 }} />
                                                                <Typography variant="body2" noWrap sx={{ fontWeight: '500' }}>
                                                                    {displayFolderName}
                                                                </Typography>
                                                            </Card>
                                                        </Grid>
                                                    )
                                                })}
                                            </Grid>
                                        </Box>
                                    )}

                                    {/* Files Grid */}
                                    {renderedFiles.length > 0 ? (
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#555' }}>
                                                Archivos ({sortedFiles.length})
                                            </Typography>
                                            <Grid container spacing={3}>
                                                {renderedFiles.map((file, index) => {
                                                    const displayName = file.name.replace(prefix, '')
                                                    const isImg = isImageFile(file)
                                                    return (
                                                        <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                                                            <Card sx={{
                                                                borderRadius: '16px',
                                                                border: '1px solid #eaeaea',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                                                height: '100%',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                transition: '0.3s',
                                                                '&:hover': {
                                                                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                                                                    transform: 'translateY(-4px)'
                                                                }
                                                            }}>
                                                                {isImg ? (
                                                                    <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#f5f5f5', cursor: 'pointer' }} onClick={() => setPreviewItem(file)}>
                                                                        <CardMedia
                                                                            component="img"
                                                                            image={file.url}
                                                                            alt={displayName}
                                                                            sx={{
                                                                                position: 'absolute',
                                                                                top: 0,
                                                                                left: 0,
                                                                                width: '100%',
                                                                                height: '100%',
                                                                                objectFit: 'cover'
                                                                            }}
                                                                        />
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ height: '140px', backgroundColor: '#F9F9F9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <FileIcon sx={{ fontSize: 50, color: '#999' }} />
                                                                    </Box>
                                                                )}

                                                                <CardContent sx={{ flexGrow: 1, padding: '16px' }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', wordBreak: 'break-all' }} lineClamp={2}>
                                                                        {displayName}
                                                                    </Typography>
                                                                    {file.contentLength && (
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ marginTop: 1 }}>
                                                                            Tamaño: {formatBytes(file.contentLength)}
                                                                        </Typography>
                                                                    )}
                                                                    {file.lastModified && (
                                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                                            Modificado: {formatDate(file.lastModified)}
                                                                        </Typography>
                                                                    )}
                                                                </CardContent>

                                                                <CardActions sx={{ justifyContent: 'flex-end', padding: '8px 16px 16px 16px' }}>
                                                                    {isImg && (
                                                                        <Button size="small" variant="outlined" onClick={() => setPreviewItem(file)} style={{ color: '#555', borderColor: '#ccc', borderRadius: '8px' }}>
                                                                            Previsualizar
                                                                        </Button>
                                                                    )}
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        onClick={() => window.open(file.url, '_blank')}
                                                                        sx={{
                                                                            backgroundColor: '#BE2E26',
                                                                            '&:hover': { backgroundColor: '#9e221b' },
                                                                            borderRadius: '8px',
                                                                            boxShadow: 'none'
                                                                        }}
                                                                    >
                                                                        {isImg ? 'Ver Original' : 'Abrir/Descargar'}
                                                                    </Button>
                                                                </CardActions>
                                                            </Card>
                                                        </Grid>
                                                    )
                                                })}
                                            </Grid>
                                        </Box>
                                    ) : (
                                        !loading && (
                                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="200px">
                                                <Typography color="textSecondary">No se encontraron archivos en este directorio.</Typography>
                                            </Box>
                                        )
                                    )}

                                    {/* Pagination / Load More button */}
                                    {(visibleCount < sortedFiles.length || continuationToken) && (
                                        <Box display="flex" justifyContent="center" sx={{ marginTop: 4 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={handleLoadMore}
                                                disabled={loadingMore}
                                                sx={{
                                                    borderColor: '#BE2E26',
                                                    color: '#BE2E26',
                                                    '&:hover': { borderColor: '#9e221b', backgroundColor: '#FFF5F5' },
                                                    borderRadius: '10px',
                                                    padding: '8px 24px'
                                                }}
                                            >
                                                {loadingMore ? 'Cargando más...' : 'Mostrar más'}
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                        </div>
                    </Card>
                </Grid>
            </Grid>

            {/* Image Preview Modal */}
            <Dialog open={!!previewItem} onClose={() => setPreviewItem(null)} maxWidth="md" fullWidth>
                {previewItem && (
                    <>
                        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '85%' }}>
                                {previewItem.name.split('/').pop()}
                            </Typography>
                            <IconButton onClick={() => setPreviewItem(null)}>
                                <Close />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ display: 'flex', justifyContent: 'center', backgroundColor: '#f0f0f0', p: 0 }}>
                            <img
                                src={previewItem.url}
                                alt={previewItem.name}
                                style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                            />
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box>
    )
}

export default GaleryPage
